import React, { useState, useRef } from 'react';
import { saveEnquiry } from '../functions/Enquiry'; // Import saveEnquiry
import ThankYouModal from './ThankYouModal'; // Import ThankYouModal

// Accept isOverlay, isHero, whiteText and onClose props
const ContactCard = ({ isOverlay = false, isHero = false, whiteText = false, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '', // changed from mobileNumber to phone
    travellerCount: '',
    travelDate: '',
    message: '',
  });

  const [showThankYou, setShowThankYou] = useState(false); // Modal state

  const formRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prepare data in the structure expected by saveEnquiry
    const dataToSave = {
      contactInfo: {
        name: formData.name,
        email: formData.email,
        phone: formData.phone, // changed from mobileNumber to phone
      },
      travellerCount: formData.travellerCount,
      travelDate: formData.travelDate,
      message: formData.message,
    };

    // Submit the hidden form (for formsubmit.co)
    if (formRef.current) {
      formRef.current.target = '';
      // Prevent navigation by using an invisible iframe as the target
      if (!document.getElementById('hidden-formsubmit-frame')) {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.name = 'hidden-formsubmit-frame';
        iframe.id = 'hidden-formsubmit-frame';
        document.body.appendChild(iframe);
      }
      formRef.current.target = 'hidden-formsubmit-frame';
      formRef.current.submit();
    }

    try {
      const result = await saveEnquiry(dataToSave);
      if (result.success) {
        setFormData({ name: '', email: '', phone: '', travellerCount: '', travelDate: '', message: '' }); // changed mobileNumber to phone
        setShowThankYou(true); // Show ThankYouModal
      } else {
        alert('Failed to send enquiry. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  // Base card style
  const baseCardStyle = {
    background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    border: 'none',
    borderRadius: '18px',
    padding: '28px 24px', // Reduced from 32px 28px
    maxWidth: '420px',
    margin: '32px auto',
    boxShadow: '0 8px 32px rgba(60,60,120,0.18)',
    fontFamily: 'Segoe UI, Arial, sans-serif',
    position: 'relative',
    overflow: 'hidden',
  };

  // Styles specific to overlay usage
  const overlayCardStyle = {
    paddingBottom: '1rem', // Reduce bottom padding instead of removing entirely
    marginBottom: '0',
    marginTop: '0', // Remove top margin as well
    boxShadow: 'none',
    borderBottomLeftRadius: '0',
    borderBottomRightRadius: '0',
    maxWidth: '100%', // Allow it to fill container width
    borderRadius: '0', // Remove all border radius if needed, or just bottom
    margin: '0', // Ensure no horizontal margin either
  };

  // Styles specific to hero placement
  const heroCardStyle = {
    margin: '0', // Remove margins for hero placement
    background: 'rgba(255, 255, 255, 0.15)', // Semi-transparent background
    backdropFilter: 'blur(10px)', // Add blur effect
    WebkitBackdropFilter: 'blur(10px)', // For Safari
    border: '1px solid rgba(255, 255, 255, 0.18)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    padding: '18px 16px', // Reduced from 24px 20px
  };

  // Combine styles based on props
  let cardStyle = baseCardStyle;
  if (isOverlay) {
    cardStyle = { ...baseCardStyle, ...overlayCardStyle };
  } else if (isHero) {
    cardStyle = { ...baseCardStyle, ...heroCardStyle };
  }

  const formGroupStyle = {
    marginBottom: '12px', // Reduced from 18px
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '4px', // Reduced from 7px
    fontWeight: 700,
    color: '#3b3b5c',
    letterSpacing: '0.02em',
    textAlign: 'left',
  };

  // Add flex row style for traveller count and travel date
  const rowGroupStyle = {
    display: 'flex',
    gap: '12px', // Reduced from 16px
    marginBottom: '12px', // Reduced from 18px
  };

  const halfInputContainer = {
    flex: 1,
    minWidth: 0,
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1.5px solid #b3b3e6',
    borderRadius: '7px',
    boxSizing: 'border-box',
    fontSize: '15px',
    background: '#f4f6fb',
    transition: 'border 0.2s',
    outline: 'none',
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: '70px', // Reduced from 100px
    resize: 'vertical',
  };

  const buttonStyle = {
    background: 'linear-gradient(45deg, #ff5e62 0%, #ff9966 100%)', // Exact sticky button gradient
    color: 'white',
    padding: '12px 0',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '17px',
    fontWeight: 600,
    width: '100%',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
    letterSpacing: '0.04em',
    transition: 'background 0.2s, transform 0.1s, box-shadow 0.2s',
  };

  const buttonHoverStyle = {
    background: 'linear-gradient(45deg, #ff4d52 0%, #ff8956 100%)', // Exact sticky button hover gradient
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
  };

  return (
    <>
      {/* Thank You Modal */}
      <ThankYouModal
        isOpen={showThankYou}
        onClose={() => {
          setShowThankYou(false);
          if (onClose) onClose(); // Close parent/contact form if handler provided
        }}
        userInfo={{ name: formData.name }}
        inline={isOverlay || isHero}
      />
      {/* Use the combined cardStyle */}
      <div style={cardStyle}>
        {/* Conditionally render title only if not in overlay or hero */}
        {!isOverlay && !isHero && (
          <h2
            style={{
              textAlign: 'center',
              marginBottom: '20px', // Reduced from 28px
              color: '#4b4b7c',
              letterSpacing: '0.05em',
              fontWeight: 700,
              fontSize: '2rem',
              textShadow: '0 2px 8px #e0e7ff',
            }}
          >
            Contact Us
          </h2>
        )}
        {/* Add a title specific for the overlay or hero */}
        {(isOverlay || isHero) && (
          <h3
            style={{
              textAlign: 'center',
              marginBottom: '14px', // Reduced from 20px
              color: whiteText ? '#ffffff' : '#3b3b5c', // Use white text if whiteText prop is true
              fontWeight: 700,
              fontSize: isHero ? '1.4rem' : '1.8rem', // Reduced hero title from 1.5rem
              textShadow: isHero ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none', // Reduced shadow opacity
            }}
          >
            Connect with an Expert
          </h3>
        )}
        {/* Hidden form for formsubmit.co */}
        <form
          ref={formRef}
          action="https://formsubmit.co/swayourwayleads@gmail.com "
          method="POST"
          target="_blank"
          style={{ display: 'none' }}
        >
          <input type="hidden" name="_template" value="table" />
          <input type="hidden" name="name" value={formData.name} />
          <input type="hidden" name="email" value={formData.email} />
          <input type="hidden" name="phone" value={formData.phone} />
          <input type="hidden" name="travellerCount" value={formData.travellerCount} />
          <input type="hidden" name="travelDate" value={formData.travelDate} />
          <input type="hidden" name="message" value={formData.message} />
          <input type="hidden" name="_captcha" value="false" />
        </form>
        <form onSubmit={handleSubmit}>
          {/* Name and Email in one row */}
          <div style={rowGroupStyle}>
            <div style={halfInputContainer}>
              <label htmlFor="name" style={labelStyle}>
                Full Name:
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                style={inputStyle}
                placeholder="Your full name"
              />
            </div>
            <div style={halfInputContainer}>
              <label htmlFor="email" style={labelStyle}>
                Email:
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={inputStyle}
                placeholder="you@email.com"
              />
            </div>
          </div>
          {/* Phone Number field (was Mobile Number) */}
          <div style={formGroupStyle}>
            <label htmlFor="phone" style={labelStyle}>
              Phone:
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              style={inputStyle}
              placeholder="Your phone number"
              pattern="[0-9]{10}"
              title="Please enter a 10-digit phone number"
            />
          </div>
          {/* Traveller Count and Travel Date in one row */}
          <div style={rowGroupStyle}>
            <div style={halfInputContainer}>
              <label htmlFor="travellerCount" style={labelStyle}>
                Traveller Count:
              </label>
              <input
                type="number"
                id="travellerCount"
                name="travellerCount"
                value={formData.travellerCount}
                onChange={handleChange}
                required
                min="1"
                style={inputStyle}
                placeholder="Number of travellers"
              />
            </div>
            <div style={halfInputContainer}>
              <label htmlFor="travelDate" style={labelStyle}>
                Travel Date:
              </label>
              <input
                type="date"
                id="travelDate"
                name="travelDate"
                value={formData.travelDate}
                onChange={handleChange}
                required
                style={inputStyle}
              />
            </div>
          </div>
          <div style={formGroupStyle}>
            <label htmlFor="message" style={labelStyle}>
              Message:
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              style={textareaStyle}
              placeholder="Message..."
            />
          </div>
          <button
            type="submit"
            style={{
              ...buttonStyle,
              background: isHero ? 'linear-gradient(45deg, #ff5e62 0%, #ff9966 100%)' : buttonStyle.background,
            }}
            onMouseOver={(e) => {
              Object.assign(e.currentTarget.style, buttonStyle, buttonHoverStyle);
            }}
            onMouseOut={(e) => {
              Object.assign(e.currentTarget.style, buttonStyle);
            }}
          >
            Send Enquiry
          </button>
        </form>
      </div>
    </>
  );
};

export default ContactCard;
